import { redactedUploadFailure } from "./uploadTelemetry";

// Exact matches only: even platform errors can carry reflected filenames or server text.
const IDB_PLATFORM_MESSAGES = new Set([
	"Database has been closed",
	"DatabaseClosedError Database has been closed",
	"Connection to Indexed Database server lost",
	"Connection to Indexed Database server lost. Refresh the page to try again",
	"Connection to Indexed Database server lost. Refresh the page to try again.",
	"db.open() was cancelled",
	"Transaction aborted",
	"Attempt to get a record without an in-progress transaction"
]);

/** Diagnostic only; never used to classify errors or decide whether to retry. */
export function indexedDbErrorChainLogDetails(error: unknown): Record<string, string | number> {
	const details: Record<string, string | number> = {};
	const visited = new Set<object>();
	let candidate = error;
	while (candidate !== null && typeof candidate === "object" && !visited.has(candidate)) {
		visited.add(candidate);
		const index = visited.size - 1;
		let name = "Error";
		let message = "Upload operation failed";
		try {
			name = redactedUploadFailure(candidate).name;
			const rawMessage = (candidate as { message?: unknown }).message;
			if (typeof rawMessage === "string" && IDB_PLATFORM_MESSAGES.has(rawMessage)) message = rawMessage;
		} catch {
			// Hostile getters lose their details, never the originating upload outcome.
		}
		details[`idbError${index}Name`] = name;
		details[`idbError${index}Message`] = message;
		try {
			candidate = (candidate as { inner?: unknown }).inner;
		} catch {
			break;
		}
	}
	// Scalar fields survive sanitizeUploadLogContext; nested arrays would be discarded.
	details.idbErrorChainLength = visited.size;
	return details;
}

export function createIndexedDbQuotaErrorPredicate(quotaExceededErrorName: string): (error: unknown) => boolean {
	return (error: unknown): boolean => {
		// Structural, not `instanceof Error`: a DOMException from another realm (or jsdom's) is not an
		// Error instance, yet its name is exactly what identifies a quota failure (LSP-21891).
		if (typeof error !== "object" || error === null) return false;
		const { name, message, inner } = error as { name?: unknown; message?: unknown; inner?: { name?: unknown } };
		return (
			(typeof message === "string" && message.trim() === quotaExceededErrorName) ||
			// A native QuotaExceededError that never passed through Dexie carries the name, not the message.
			name === "QuotaExceededError" ||
			inner?.name === "QuotaExceededError"
		);
	};
}
